import { CandidatLink } from './candidat/CandidatLink';
import React, { useState, useEffect, useCallback , useMemo } from 'react';
import axios from 'axios';
import { qcmTypeLabel } from '../lib/qcmTypes';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  FileText, Award, Clock, Users, Plus, Trash2, ChevronRight,
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, BarChart3, ExternalLink
} from 'lucide-react';
import { getAuthToken } from '../lib/authToken';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QCMFormateurPanel = ({ dossierId }) => {
  const [assignments, setAssignments] = useState([]);
  const [availableQcms, setAvailableQcms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedQcmId, setSelectedQcmId] = useState('');
  const [dateLimite, setDateLimite] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailView, setDetailView] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${getAuthToken()}` }), []);

  const fetchData = useCallback(async () => {
    try {
      const [assignRes, qcmsRes] = await Promise.all([
        axios.get(`${API_URL}/qcms/assignments/${dossierId}`, { headers }),
        axios.get(`${API_URL}/qcms`, { headers }),
      ]);
      setAssignments(assignRes.data);
      setAvailableQcms(qcmsRes.data.filter(q => q.statut === 'publie'));
    } catch { }
    setLoading(false);
  }, [dossierId, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAssign = async () => {
    if (!selectedQcmId) { toast.error('Sélectionnez un QCM'); return; }
    setAssigning(true);
    try {
      await axios.post(`${API_URL}/qcms/assign`, {
        qcm_id: selectedQcmId,
        dossier_id: dossierId,
        date_limite: dateLimite || null,
      }, { headers });
      toast.success('QCM assigné aux candidats');
      setShowAssignDialog(false);
      setSelectedQcmId('');
      setDateLimite('');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setAssigning(false);
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/qcms/assignments/${deleteTarget.id}`, { headers });
      toast.success('QCM retiré');
      await fetchData();
    } catch { toast.error('Erreur'); }
    setDeleteTarget(null);
  };

  const openDetail = async (assignment) => {
    setDetailView(assignment);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/qcms/${assignment.qcm_id}/resultats-dossier/${dossierId}`, { headers });
      setDetailData(res.data);
    } catch { toast.error('Erreur chargement résultats'); }
    setDetailLoading(false);
  };

  // Already assigned QCM IDs
  const assignedQcmIds = new Set(assignments.map(a => a.qcm_id));
  const unassignedQcms = availableQcms.filter(q => !assignedQcmIds.has(q.id));

  if (detailView && detailData) {
    const passages = detailData.passages || [];
    const nonPasses = detailData.non_passes || [];
    const absents = detailData.absents || [];
    return (
      <div className="space-y-4" data-testid="qcm-detail-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setDetailView(null); setDetailData(null); }}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h3 className="font-semibold text-slate-800">{detailView.qcm_titre}</h3>
          <Badge variant="outline" className="text-xs">
            {passages.length}/{detailView.total_candidats} passé(s)
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-indigo-600">{passages.length}</p>
              <p className="text-xs text-slate-500">Passé(s)</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {passages.filter(p => p.pourcentage != null).length > 0 ? Math.round(passages.filter(p => p.pourcentage != null).reduce((s, p) => s + p.pourcentage, 0) / passages.filter(p => p.pourcentage != null).length) : 0}%
              </p>
              <p className="text-xs text-slate-500">Moyenne</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{nonPasses.length}</p>
              <p className="text-xs text-slate-500">En attente</p>
            </CardContent>
          </Card>
        </div>

        {/* Results table */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="qcm-results-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-2.5 px-4 font-medium text-slate-600">Candidat</th>
                  <th className="text-center py-2.5 px-4 font-medium text-slate-600">Score</th>
                  <th className="text-center py-2.5 px-4 font-medium text-slate-600">%</th>
                  <th className="text-center py-2.5 px-4 font-medium text-slate-600">Résultat</th>
                  <th className="text-right py-2.5 px-4 font-medium text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {passages.map(p => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-2.5 px-4 font-medium text-slate-800"><CandidatLink dossierId={dossierId} candidatId={p.candidat_id}>{p.candidat_nom}</CandidatLink></td>
                    <td className="py-2.5 px-4 text-center text-slate-600">{p.pourcentage == null ? '—' : `${p.score}/${p.score_max}`}</td>
                    <td className="py-2.5 px-4 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        p.pourcentage == null ? 'bg-slate-100 text-slate-500' :
                        p.pourcentage >= 70 ? 'bg-emerald-50 text-emerald-700' :
                        p.pourcentage >= 50 ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }`}>
                        {p.pourcentage == null ? 'Non répondu' : `${p.pourcentage}%`}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-center">
                      {p.reussi === true && <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />}
                      {p.reussi === false && <XCircle className="w-4 h-4 text-red-500 mx-auto" />}
                      {p.reussi === null && <span className="text-slate-300">-</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-xs text-slate-400">
                      {p.completed_at ? new Date(p.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                  </tr>
                ))}
                {nonPasses.map(np => (
                  <tr key={np.candidat_id} className="border-b last:border-0 bg-slate-50/50">
                    <td className="py-2.5 px-4 font-medium text-slate-400"><CandidatLink dossierId={dossierId} candidatId={np.candidat_id}>{np.candidat_nom}</CandidatLink></td>
                    <td className="py-2.5 px-4 text-center text-slate-300">-</td>
                    <td className="py-2.5 px-4 text-center text-slate-300">-</td>
                    <td className="py-2.5 px-4 text-center">
                      <span className="text-xs text-slate-400 italic">En attente</span>
                    </td>
                    <td className="py-2.5 px-4 text-right text-slate-300">-</td>
                  </tr>
                ))}
                {absents.map(np => (
                  <tr key={np.candidat_id} className="border-b last:border-0 bg-red-50/40" data-testid={`qcm-absent-row-${np.candidat_id}`}>
                    <td className="py-2.5 px-4 font-medium text-slate-400"><CandidatLink dossierId={dossierId} candidatId={np.candidat_id}>{np.candidat_nom}</CandidatLink></td>
                    <td className="py-2.5 px-4 text-center text-slate-300">-</td>
                    <td className="py-2.5 px-4 text-center text-slate-300">-</td>
                    <td className="py-2.5 px-4 text-center"><span className="text-xs text-red-500 italic">Absent — non évalué</span></td>
                    <td className="py-2.5 px-4 text-right text-slate-300">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {passages.length === 0 && nonPasses.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-400">Aucun résultat disponible</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="qcm-formateur-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <FileText className="w-4 h-4 text-indigo-500" />
          QCM assignés
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAssignDialog(true)}
          data-testid="assign-qcm-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Assigner un QCM
        </Button>
      </div>

      {/* Assignments list */}
      {loading ? (
        <p className="text-sm text-slate-400 text-center py-6">Chargement...</p>
      ) : assignments.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-10 text-center">
            <FileText className="w-10 h-10 mx-auto mb-2 text-slate-200" />
            <p className="text-sm text-slate-500">Aucun QCM assigné à cette action</p>
            <p className="text-xs text-slate-400 mt-1">Assignez un QCM publié pour évaluer les candidats</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card
              key={a.id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openDetail(a)}
              data-testid={`assignment-card-${a.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-slate-800 truncate">{a.qcm_titre}</h4>
                      {a.type_evaluation && (
                        <Badge variant="outline" className="text-[10px]">{qcmTypeLabel(a.type_evaluation)}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {a.nb_questions} questions</span>
                      {a.settings?.temps_limite_minutes && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.settings.temps_limite_minutes} min</span>
                      )}
                      {a.settings?.seuil_reussite && (
                        <span className="flex items-center gap-1"><Award className="w-3 h-3" /> Seuil {a.settings.seuil_reussite}%</span>
                      )}
                    </div>
                  </div>

                  {/* Progress indicator */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className="text-lg font-bold text-indigo-600">{a.nb_completed}/{a.total_candidats}</p>
                      <p className="text-[10px] text-slate-400">passé(s)</p>
                    </div>
                    {a.nb_completed > 0 && (
                      <div className="text-right">
                        <p className={`text-lg font-bold ${a.moyenne >= 70 ? 'text-emerald-600' : a.moyenne >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {a.moyenne}%
                        </p>
                        <p className="text-[10px] text-slate-400">moyenne</p>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(a); }}
                        className="text-red-300 hover:text-red-600"
                        data-testid={`remove-assignment-${a.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent data-testid="assign-qcm-dialog">
          <DialogHeader>
            <DialogTitle>Assigner un QCM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>QCM à assigner</Label>
              {unassignedQcms.length === 0 ? (
                <p className="text-sm text-slate-400 mt-2 italic">Tous les QCM publiés sont déjà assignés, ou aucun QCM publié n'est disponible.</p>
              ) : (
                <Select value={selectedQcmId} onValueChange={setSelectedQcmId}>
                  <SelectTrigger className="mt-1" data-testid="select-qcm-assign">
                    <SelectValue placeholder="Sélectionnez un QCM..." />
                  </SelectTrigger>
                  <SelectContent>
                    {unassignedQcms.map(q => (
                      <SelectItem key={q.id} value={q.id}>
                        {q.titre} ({q.questions?.length || 0} questions)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Date limite (optionnel)</Label>
              <Input
                type="date"
                value={dateLimite}
                onChange={(e) => setDateLimite(e.target.value)}
                className="mt-1"
                data-testid="date-limite-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Annuler</Button>
            <Button onClick={handleAssign} disabled={assigning || !selectedQcmId} data-testid="confirm-assign-btn">
              {assigning ? 'Assignation...' : 'Assigner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce QCM ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le QCM "{deleteTarget?.qcm_titre}" sera retiré de cette action. Les résultats déjà enregistrés ne seront pas supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-red-600 hover:bg-red-700">
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default QCMFormateurPanel;
